import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { socialExport, webApi } from '@mi-front/api-clients';
import { GetFragmentDto } from '@mi-front/core';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SendIcon from '@mui/icons-material/Send';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface UseSocialExportUploadItemCardActionsParams {
  upload: webApi.FragmentSocialUploadItemDto;
  fragmentId: string;
  fragment: GetFragmentDto;
}

/**
 * Hook that provides trigger button and menu/dialog for upload card actions.
 * Used so the trigger can be placed inside a ButtonGroup while menu and dialog render as siblings.
 */
export function useSocialExportUploadItemCardActions({
  upload,
  fragmentId,
  fragment,
}: UseSocialExportUploadItemCardActionsParams) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const status = upload.status;
  const platformFeatures = upload.platformFeatures;
  const platform = socialPlatforms.get(upload.platform);

  const isCompleted = status === webApi.FragmentSocialUploadItemDtoStatusEnum.COMPLETED;
  const isScheduled = status === webApi.FragmentSocialUploadItemDtoStatusEnum.SCHEDULED;
  const isFailed = status === webApi.FragmentSocialUploadItemDtoStatusEnum.FAILED;
  const hasPublicUrl = Boolean(platformFeatures?.publicUrl);
  const isWaitingPublicLink = isCompleted && !hasPublicUrl;
  const pending = webApi.FragmentSocialUploadItemDtoStatusEnum;
  const isInProgress =
    status === pending.WAITING || status === pending.WAITING_CHILDREN || status === pending.IN_PROGRESS;

  const deleteMutation = socialExport.useDeleteUpload({
    mutation: {
      onSuccess: async (_: socialExport.DeleteUpload200, variables: { id: string }) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: webApi.getFragmentSocialUploadsByFragmentIdQueryKey(fragmentId),
          }),
          queryClient.invalidateQueries({
            queryKey: socialExport.getUploadQueryKey(variables.id),
          }),
        ]);
        notifySuccess(
          $t({
            id: 'fragments.socialExport.videoDeletedFromPlatform',
            defaultMessage: 'Video eliminado de la plataforma',
          }),
        );
      },
      onError: (error: Error) => {
        notifyError(
          error?.message ||
            $t({
              id: 'fragments.socialExport.errorDeletingVideo',
              defaultMessage: 'Ocurrió un error al eliminar el video',
            }),
        );
      },
    },
  });

  const promoteMutation = socialExport.usePromoteUpload({
    mutation: {
      onSuccess: async (_: socialExport.PromoteUpload200, variables: { id: string }) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: webApi.getFragmentSocialUploadsByFragmentIdQueryKey(fragmentId),
          }),
          queryClient.invalidateQueries({
            queryKey: socialExport.getUploadQueryKey(variables.id),
          }),
        ]);
        notifySuccess(
          $t({
            id: 'fragments.socialExport.publicationStarted',
            defaultMessage: 'Publicación iniciada',
          }),
        );
      },
      onError: (error: Error) => {
        notifyError(
          error?.message ||
            $t({
              id: 'fragments.socialExport.errorPublishingNow',
              defaultMessage: 'Ocurrió un error al publicar inmediatamente',
            }),
        );
      },
    },
  });

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isWaitingPublicLink || isInProgress) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isWaitingPublicLink || isInProgress) {
      handleClose();
    }
    // We intentionally close the menu whenever the upload starts waiting for a public link.
    // This prevents stale anchors from keeping the menu open during polling updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInProgress, isWaitingPublicLink]);

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const handlePromote = async () => {
    await promoteMutation.mutateAsync({ id: upload.id });
  };

  const handleRetry = () => {
    handleClose();
    pushTo(routeGenerator.upload(fragment, platform));
  };

  const hasManageUrl =
    platformFeatures &&
    platformFeatures.manageUrl &&
    platformFeatures.manageUrl !== platformFeatures.publicUrl;

  const hasCompletedMenuItems =
    isCompleted && ((hasManageUrl && platformFeatures?.manageUrl) || platformFeatures?.isDeletable);
  const hasScheduledMenuItems = isScheduled;
  const hasFailedMenuItems = isFailed;

  const hasAnyMenuItems = hasCompletedMenuItems || hasScheduledMenuItems || hasFailedMenuItems;

  const trigger = isWaitingPublicLink || isInProgress ? null : (
    <Button
      size="small"
      variant="outlined"
      onClick={handleOpen}
      sx={{ minWidth: 0, px: 1 }}
    >
      <MoreHorizIcon fontSize="small" />
    </Button>
  );

  const menuAndDialog = isWaitingPublicLink || isInProgress ? null : (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open && !isWaitingPublicLink}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!hasAnyMenuItems ? (
          <MenuItem disabled sx={{ cursor: 'default' }}>
            <ListItemText>
              <FormattedMessage
                id="fragments.socialExport.noAdditionalActions"
                defaultMessage="La plataforma no permite ninguna acción adicional"
              />
            </ListItemText>
          </MenuItem>
        ) : (
          <>
            {isCompleted && hasManageUrl && platformFeatures?.manageUrl && (
              <MenuItem
                component="a"
                href={platformFeatures.manageUrl}
                target="_blank"
                rel="noreferrer"
                onClick={handleClose}
              >
                <ListItemIcon>
                  <BuildIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <FormattedMessage
                    id="fragments.socialExport.manageVideo"
                    defaultMessage="Administrar video en {platform}"
                    values={{ platform: platform.name }}
                  />
                </ListItemText>
              </MenuItem>
            )}
            {isCompleted && platformFeatures?.isDeletable && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  setDeleteDialogOpen(true);
                }}
                disabled={deleteMutation.isPending}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>
                  <FormattedMessage
                    id="fragments.socialExport.deleteFromPlatform"
                    defaultMessage="Eliminar de {platform}"
                    values={{ platform: platform.name }}
                  />
                </ListItemText>
              </MenuItem>
            )}
            {isScheduled && (
              <>
                <ConfirmationAction
                  title={$t({
                    id: 'fragments.socialExport.publishNowConfirmTitle',
                    defaultMessage: 'Publicar ahora',
                  })}
                  message={
                    <FormattedMessage
                      id="fragments.socialExport.publishNowConfirmMessage"
                      defaultMessage="¿Estás seguro de que deseas publicar este video ahora en lugar de esperar a la fecha programada?"
                    />
                  }
                  confirmLabel={$t({
                    id: 'fragments.socialExport.publishNow',
                    defaultMessage: 'Publicar ahora',
                  })}
                  cancelLabel={$t({
                    id: 'common.cancel',
                    defaultMessage: 'Cancelar',
                  })}
                  confirmColor="primary"
                  onConfirm={handlePromote}
                >
                  {({ onClick }) => (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        onClick();
                      }}
                      disabled={promoteMutation.isPending}
                    >
                      <ListItemIcon>
                        <SendIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        <FormattedMessage
                          id="fragments.socialExport.publishNow"
                          defaultMessage="Publicar ahora"
                        />
                      </ListItemText>
                    </MenuItem>
                  )}
                </ConfirmationAction>
                <ConfirmationAction
                  title={$t({
                    id: 'fragments.socialExport.deleteScheduledPublishTitle',
                    defaultMessage: 'Eliminar publicación programada',
                  })}
                  message={
                    <FormattedMessage
                      id="fragments.socialExport.deleteScheduledPublishMessage"
                      defaultMessage="¿Estás seguro de que deseas eliminar esta publicación programada? Esta acción no se puede deshacer."
                    />
                  }
                  confirmLabel={$t({
                    id: 'common.delete',
                    defaultMessage: 'Eliminar',
                  })}
                  cancelLabel={$t({
                    id: 'common.cancel',
                    defaultMessage: 'Cancelar',
                  })}
                  confirmColor="error"
                  onConfirm={handleDelete}
                >
                  {({ onClick }) => (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        onClick();
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
                      </ListItemText>
                    </MenuItem>
                  )}
                </ConfirmationAction>
              </>
            )}
            {isFailed && (
              <MenuItem onClick={handleRetry}>
                <ListItemIcon>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <FormattedMessage id="fragments.socialExport.retry" defaultMessage="Reintentar" />
                </ListItemText>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {$t(
            {
              id: 'fragments.socialExport.deleteVideoFromPlatformTitle',
              defaultMessage: 'Eliminar video de {platform}',
            },
            { platform: platform.name },
          )}
        </DialogTitle>
        <DialogContent>
          <FormattedMessage
            id="fragments.socialExport.deleteVideoFromPlatformMessage"
            defaultMessage="¿Estás seguro de que deseas eliminar este video de la plataforma? Esta acción no se puede deshacer."
          />
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {$t({
              id: 'common.cancel',
              defaultMessage: 'Cancelar',
            })}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={async () => {
              await handleDelete();
              setDeleteDialogOpen(false);
            }}
          >
            {$t({
              id: 'common.delete',
              defaultMessage: 'Eliminar',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { trigger, menuAndDialog };
}
